// Projects (Phase 5): the big rocks. Each project row shows synopsis +
// owner + due date on the left, the step checklist in the middle (click
// to toggle; progress derives from done/total), and the progress ring.

import { useState } from 'react';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import { Card, Eyebrow, Pill } from '../ui';
import { parseLocalDate } from '../dates';

export const PROJECT_STATUS = {
  not_started: { label: 'Not started', tone: 'warn' },
  in_progress: { label: 'In progress', tone: 'neutral' },
  blocked: { label: 'Blocked', tone: 'crit' },
  done: { label: 'Done', tone: 'good' },
};

export default function ProjectsView() {
  const { projects, reloadProjects } = useMarketing();
  const [editing, setEditing] = useState(null);   // project object or 'new'
  const [err, setErr] = useState(null);

  const list = projects.data || [];

  async function toggleStep(project, stepId) {
    setErr(null);
    try {
      const steps = project.steps.map(s => s.id === stepId ? { ...s, done: !s.done } : s);
      const res = await apiFetch(`/api/data/projects/${project.id}/steps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await reloadProjects();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function remove(project) {
    if (!window.confirm(`Delete project "${project.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/data/projects/${project.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await reloadProjects();
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Eyebrow>Projects · the big rocks</Eyebrow>
        <button
          onClick={() => setEditing('new')}
          className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-3.5 py-2 hover:bg-bullogic-blue/5"
        >
          + New project
        </button>
      </div>

      {err && <div className="text-[12px] text-rose">{err}</div>}
      {projects.loading && <div className="text-[12px] text-ink-3">Loading projects…</div>}

      {!projects.loading && list.length === 0 && (
        <Card className="p-5">
          <div className="text-[13px] text-ink-2">
            No projects yet. The big rocks live here: lead magnet builds, quarter planning,
            template refreshes. Each gets a step checklist that drives its progress.
          </div>
        </Card>
      )}

      {list.length > 0 && (
        <Card>
          {list.map((p, i) => {
            const st = PROJECT_STATUS[p.status] || PROJECT_STATUS.not_started;
            const pct = Math.round(p.progress * 100);
            const doneSteps = p.steps.filter(s => s.done).length;
            return (
              <div key={p.id} className={`grid grid-cols-[1.5fr_1.3fr_150px] gap-4 items-start p-4 border-b border-line-2 last:border-b-0 ${i === 0 ? 'rounded-t-[8px]' : ''}`}>
                <div>
                  <h3 className="text-navy text-[15px] font-black">{p.name}</h3>
                  {p.synopsis && <div className="text-ink-2 text-[12.5px] mt-1 leading-relaxed">{p.synopsis}</div>}
                  <div className="flex gap-2 mt-2 flex-wrap items-center">
                    {p.ownerEmail && <Tag>Owner: {p.ownerEmail.split('@')[0]}</Tag>}
                    {p.dueDate && <Tag>Due {parseLocalDate(p.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Tag>}
                    <Pill tone={st.tone} className="text-[10px]">{st.label}</Pill>
                    <button onClick={() => setEditing(p)} className="text-[10px] font-bold text-bullogic-blue hover:underline">edit</button>
                    <button onClick={() => remove(p)} className="text-[10px] font-bold text-rose/70 hover:underline">delete</button>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 pt-1">
                  {p.steps.map(s => (
                    <button key={s.id} onClick={() => toggleStep(p, s.id)} className="flex items-center gap-2 text-left group">
                      <span className={`w-[14px] h-[14px] rounded-[4px] border-[1.6px] grid place-items-center text-[9px] leading-none shrink-0 ${
                        s.done ? 'bg-deep-teal border-deep-teal text-white' : 'border-ink-3 group-hover:border-deep-teal bg-white'
                      }`}>{s.done ? '✓' : ''}</span>
                      <span className={`text-[12.5px] ${s.done ? 'text-ink-3' : 'text-ink'}`}>{s.label}</span>
                    </button>
                  ))}
                  {p.steps.length === 0 && <span className="text-[12px] text-ink-3 italic">no steps yet</span>}
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-[52px] h-[52px] rounded-full grid place-items-center"
                    style={{ background: `conic-gradient(#006D78 ${pct}%, #eef0f4 0)` }}
                  >
                    <span className="w-[40px] h-[40px] rounded-full bg-white grid place-items-center font-black text-[12px] text-navy">
                      {pct}%
                    </span>
                  </div>
                  <span className="text-ink-2 text-[11px]">
                    {p.steps.length > 0 ? `${doneSteps} of ${p.steps.length} steps` : PROJECT_STATUS[p.status]?.label}
                  </span>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {editing && (
        <ProjectModal
          project={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reloadProjects(); }}
        />
      )}
    </div>
  );
}

function Tag({ children }) {
  return <span className="text-[11px] font-bold text-ink-2 bg-line-2 px-2 py-px rounded-[5px] whitespace-nowrap">{children}</span>;
}

function ProjectModal({ project, onClose, onSaved }) {
  const [name, setName] = useState(project?.name || '');
  const [synopsis, setSynopsis] = useState(project?.synopsis || '');
  const [dueDate, setDueDate] = useState(project?.dueDate ? String(project.dueDate).slice(0, 10) : '');
  const [status, setStatus] = useState(project?.status || 'not_started');
  const [stepsText, setStepsText] = useState((project?.steps || []).map(s => s.label).join('\n'));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const stepLabels = stepsText.split('\n').map(l => l.trim()).filter(Boolean);
      if (project) {
        let res = await apiFetch(`/api/data/projects/${project.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim(), synopsis: synopsis.trim() || null, dueDate: dueDate || null, status }),
        });
        let data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        // Preserve done flags for steps whose label survived the edit.
        const doneByLabel = Object.fromEntries((project.steps || []).map(s => [s.label, s.done]));
        res = await apiFetch(`/api/data/projects/${project.id}/steps`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ steps: stepLabels.map(label => ({ label, done: Boolean(doneByLabel[label]) })) }),
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      } else {
        const res = await apiFetch('/api/data/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim(), synopsis: synopsis.trim() || null, dueDate: dueDate || null, status, steps: stepLabels }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      }
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[460px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[14px] mb-3">{project ? `Edit "${project.name}"` : 'New project'}</div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Name</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} className={input} />
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Synopsis</label>
        <textarea value={synopsis} onChange={e => setSynopsis(e.target.value)} rows={2} className={input} />
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Due date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={input} />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={input}>
              {Object.entries(PROJECT_STATUS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Steps (one per line, in order)</label>
        <textarea value={stepsText} onChange={e => setStepsText(e.target.value)} rows={5} className={input} placeholder={'Outline + offer\nCopy drafted\nDesign\nLanding page'} />
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-3.5">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !name.trim()}
            className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save project'}
          </button>
        </div>
      </div>
    </div>
  );
}
